import api from './api';

class CategoriesService {
  // Get all categories with filters and pagination
  async getCategories(params = {}) {
    try {
      const response = await api.get('/categories', { params });
      return response.data;
    } catch (error) {
      // Handle 501 (not implemented) by returning mock data
      if (error.response?.status === 501) {
        console.warn('Categories endpoint not implemented, returning mock data');
        return this.getMockCategories(params);
      }
      throw this.handleError(error);
    }
  }

  // Get category by ID
  async getCategory(id) {
    try {
      const response = await api.get(`/categories/${id}`);
      return response.data.category;
    } catch (error) {
      // Handle 501 (not implemented) by returning mock data
      if (error.response?.status === 501) {
        console.warn('Category detail endpoint not implemented, returning mock data');
        return this.getMockCategory(id);
      }
      throw this.handleError(error);
    }
  }

  // Create new category
  async createCategory(data) {
    try {
      const response = await api.post('/categories', data);
      return response.data.category;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update category
  async updateCategory(id, data) {
    try {
      const response = await api.put(`/categories/${id}`, data);
      return response.data.category;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete category
  async deleteCategory(id) {
    try {
      const response = await api.delete(`/categories/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Toggle category status
  async toggleCategoryStatus(id) {
    try {
      const response = await api.put(`/categories/${id}/toggle`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get category packages
  async getCategoryPackages(id, params = {}) {
    try {
      const response = await api.get(`/categories/${id}/packages`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Reorder categories
  async reorderCategories(data) {
    try {
      const response = await api.put('/categories/reorder', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Upload category icon
  async uploadIcon(categoryId, file) {
    try {
      const formData = new FormData();
      formData.append('icon', file);

      const response = await api.post(`/categories/${categoryId}/icon`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get category hierarchy (tree structure)
  async getCategoryHierarchy() {
    try {
      const response = await api.get('/categories/hierarchy');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get category statistics
  async getCategoryStats() {
    try {
      const response = await api.get('/categories/stats');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get category status options
  getStatusOptions() {
    return [
      { value: 'active', label: 'Active', color: '#4caf50' },
      { value: 'inactive', label: 'Inactive', color: '#f44336' },
    ];
  }

  // Mock data methods for when endpoints are not implemented
  getMockCategories(params = {}) {
    const mockCategories = [
      {
        id: 1,
        name: 'Umrah Packages',
        description: 'Complete Umrah pilgrimage packages',
        slug: 'umrah-packages',
        status: 'active',
        package_count: 25,
        sort_order: 1,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-15T00:00:00Z'
      },
      {
        id: 2,
        name: 'Hajj Packages',
        description: 'Complete Hajj pilgrimage packages',
        slug: 'hajj-packages',
        status: 'active',
        package_count: 12,
        sort_order: 2,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-10T00:00:00Z'
      },
      {
        id: 3,
        name: 'Premium Tours',
        description: 'Luxury pilgrimage experiences',
        slug: 'premium-tours',
        status: 'active',
        package_count: 8,
        sort_order: 3,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-05T00:00:00Z'
      },
      {
        id: 4,
        name: 'Budget Packages',
        description: 'Affordable pilgrimage options',
        slug: 'budget-packages',
        status: 'active',
        package_count: 15,
        sort_order: 4,
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      }
    ];

    // Apply pagination
    const page = parseInt(params.page) || 1;
    const limit = parseInt(params.limit) || 20;
    const offset = (page - 1) * limit;
    
    // Apply filters
    let filteredCategories = mockCategories;
    if (params.status) {
      filteredCategories = filteredCategories.filter(cat => cat.status === params.status);
    }
    if (params.search) {
      const searchTerm = params.search.toLowerCase();
      filteredCategories = filteredCategories.filter(cat => 
        cat.name.toLowerCase().includes(searchTerm) ||
        cat.description.toLowerCase().includes(searchTerm)
      );
    }

    const total = filteredCategories.length;
    const categories = filteredCategories.slice(offset, offset + limit);
    
    return {
      categories,
      pagination: {
        page,
        limit,
        total,
        pages: Math.ceil(total / limit)
      }
    };
  }

  getMockCategory(id) {
    const mockCategories = this.getMockCategories().categories;
    const category = mockCategories.find(c => c.id === parseInt(id));
    return category || null;
  }

  // Handle API errors
  handleError(error) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    } else if (error.message) {
      return new Error(error.message);
    } else {
      return new Error('An unexpected error occurred');
    }
  }
}

export default new CategoriesService();